import { useState, type ReactNode } from 'react'
import { SignIn } from '@clerk/clerk-react'
import { cn } from '@loop3/ui'
import logoUrl from './assets/loop3-logo.svg'

export interface SignInScreenProps {
  /** Decorative full-bleed layer behind the card. Hidden below `lg`. */
  background?: ReactNode
  className?: string
}

const APPEARANCE = {
  layout: {
    logoImageUrl: logoUrl,
    logoPlacement: 'inside' as const,
  },
  elements: {
    rootBox: 'w-full',
    card: 'w-full shadow-sm border border-border rounded-lg bg-card',
    logoBox: 'h-10 mb-2',
    logoImage: 'h-10',
    headerTitle: 'font-display font-light text-xl',
    headerSubtitle: 'text-sm opacity-60',
    socialButtonsBlockButton:
      'border border-border hover:bg-[var(--l3-violet-soft)] hover:border-[var(--l3-violet)] transition-colors',
  },
}

/**
 * The signed-out screen for every L3 SPA: Clerk's sign-in card, Loop3-branded
 * and centred on the viewport. Mounted without a `path` prop so Clerk routes
 * its own multi-step flows and consumers need no extra routes. A completed
 * sign-in returns the visitor to the URL they opened.
 */
export function SignInScreen({ background, className }: SignInScreenProps) {
  // Captured once: Clerk's hash router rewrites the hash as the flow advances.
  const [returnUrl] = useState(() => window.location.href.split('#')[0])

  return (
    <div
      className={cn(
        'relative flex min-h-screen items-center justify-center bg-background p-6',
        className
      )}
    >
      {background ? (
        <div data-slot="sign-in-background" className="absolute inset-0 hidden lg:block">
          {background}
        </div>
      ) : null}
      <div className="relative z-10 flex w-full max-w-sm flex-col items-center">
        <SignIn
          appearance={APPEARANCE}
          fallbackRedirectUrl={returnUrl}
          signUpFallbackRedirectUrl={returnUrl}
        />
      </div>
    </div>
  )
}
